// Detection des seances en double : deux notations sont equivalentes si
// elles decrivent la meme suite d'etapes (memes allures, memes durees /
// distances, memes recuperations), quelle que soit l'ecriture : "1km" et
// "1000m", "5min" et "5'", "SEMI" et "HP", "2x5x(...)" et "10x(...)", etc.
// On s'appuie sur la mise a plat de la seance faite pour intervals.icu.
import { type ZoneRow } from './allureTable.ts';
import { etapesSeance, type EtapeIcu } from './intervalsIcu.ts';
import { categorieDe } from './composition.ts';
import { type Seance } from './seancesApi.ts';

/** Empreinte d'une notation (null si vide ou invalide). */
export function empreinteNotation(notation: string | null | undefined, vma: number, table: ZoneRow[]): string | null {
  if (!notation || !notation.trim()) return null;
  try {
    const etapes = etapesSeance(notation, vma > 0 ? vma : 16.5, table);
    if (etapes.length === 0) return null;
    return etapes
      .map((e: EtapeIcu) => {
        const d = e.distanceM !== undefined ? `${Math.round(e.distanceM)}m` : `${Math.round(e.secondes ?? 0)}s`;
        return e.type === 'effort' ? `${e.code}${e.condition ? `[${e.condition}]` : ''}:${d}` : `R:${d}`;
      })
      .join('|');
  } catch {
    return null;
  }
}

/**
 * Seances enregistrees equivalentes a une notation (hors seance `idExclu`,
 * ex : celle en cours d'edition). Les modeles (sans notation) sont ignores.
 */
export function seancesIdentiques(
  notation: string,
  seances: Seance[],
  vma: number,
  table: ZoneRow[],
  idExclu?: string,
): Seance[] {
  const cle = empreinteNotation(notation, vma, table);
  if (!cle) return [];
  return seances.filter(
    (s) => s.id !== idExclu && categorieDe(s) !== 'MODELE' && empreinteNotation(s.notation, vma, table) === cle,
  );
}

/** Nom d'une seance dans un message de doublon : « Nom », suivi de (socle) pour le socle commun. */
export function nomDoublon(seance: Seance): string {
  return `« ${seance.nom} »${seance.portee === 'SOCLE' ? ' (socle)' : ''}`;
}

/** Pour chaque seance qui a des doublons : les autres seances identiques. */
export function doublonsParSeance(seances: Seance[], vma: number, table: ZoneRow[]): Map<string, Seance[]> {
  const groupes = new Map<string, Seance[]>();
  seances.forEach((s) => {
    if (!s.id || categorieDe(s) === 'MODELE') return;
    const cle = empreinteNotation(s.notation, vma, table);
    if (!cle) return;
    groupes.set(cle, [...(groupes.get(cle) ?? []), s]);
  });
  const resultat = new Map<string, Seance[]>();
  groupes.forEach((groupe) => {
    if (groupe.length < 2) return;
    groupe.forEach((s) => resultat.set(s.id as string, groupe.filter((x) => x.id !== s.id)));
  });
  return resultat;
}
